/* eslint-disable no-console */
import React from 'react';
import ReactDom from 'react-dom';

import {handleDefaults, getQueryParams} from '../../helpers/utils';
import Resources from '../../helpers/resources';
import {Credentials} from '../../types';

export default class BasicApp extends React.Component {
    element?: any; // ts-ignore overload error

    options?: {};

    constructor(element: string, dynamicOptions: {}) {
        super(
            null as any //strict
        );
        BasicApp.configure();
        this.element = element;
        this.options = handleDefaults({}, dynamicOptions);
        this.renderElement();
    }

    // This render function should get overridden in your entry app
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    render() {
        return <h1>You should define a render() method for your subclass of BasicApp</h1>;
    }

    // Implementation details.  You should not need to change any code past this point.

    renderElement() {
        ReactDom.render(
            this.render(),
            document.querySelector(this.element)
        );
    }

    static configure() {
        // How we want to configure things depends on where this app is running,
        // and where the services we want to access live.
        //
        // 1. When running on a developer's machine, using a mock server, we want
        //    to use a hard-coded config from
        //
        //       helpers/local-resource-config.js
        //
        // 2. When running on a developer's machine, but we want to use back-end
        //    services on demo, QA or live, we want to get the configuration from
        //    the configuration of the host app.
        //
        // 3. When running on demo, QA or live, the configuration will be injected
        //    based on the host app's configuration, but can be overridden by query
        //    parameters.
        //
        // We determine which situation we have by looking at the window location
        // URL and the query parameters, and whether there is a pppAppConfig property
        // on the window object.

        const {location} = window;
        const runningOnDeveloperMachine = /^https?:\/\/localhost/.test(location.toString());
        const params: {} = getQueryParams(location.search);
        // hash is in password not parseable
        // A lot of C3 accounts use a hash in the password, that's not supported.
        // So just hard code it here for development.
        //
        // TODO refactor boilerplate to accept hash is in password
        // Its non trivial as existing code depends on location.search
        //
        // @ts-ignore
        // params.username = '';
        // @ts-ignore
        // params.password = '';
        // @ts-ignore
        // params.env = '';
        //
        if (runningOnDeveloperMachine) {
            BasicApp.configureForDeveloperMachine(params);
        } else {
            BasicApp.configureForRemoteHost(params);
        }
    }

    static configureForDeveloperMachine(params: { env?: string } & Credentials) {
        let envName = params.env;

        if (!envName) {
            console.log(`BasicApp.configureForDeveloperMachine() 
            - you did not provide an "env" query parameter, so I am assuming you want to talk to live.`);

            envName = 'live';
        }

        const credentials = BasicApp.getCredentials(params);

        if (envName === 'local' || BasicApp.weHaveUsableCredentials(credentials)) {
            Resources.initialise(envName, credentials);
        }
    }

    static configureForRemoteHost(params: {}) {
        const config = (window as any)._env_;
        const envName = config?.env || 'live';
        const credentials = BasicApp.getCredentials(params);

        if (!config.BaseName) {
            console.log(
                'BasicApp.configureForRemoteHost() - your pppAppConfig does not'
                + ' include a BaseName item; we will assume it should be blank.'
            );

            config.BaseName = '';
        }

        if (BasicApp.weHaveUsableCredentials(credentials)) {
            Resources.initialise(envName, credentials);
            Resources.env.setConfiguration(config);
        }
    }

    static getCredentials(params: Credentials) {
        const credentials: Credentials = {
            authToken: undefined,
            JWT: undefined,
            username: undefined,
            password: undefined
        };
        const items: (keyof Credentials)[] = ['authToken', 'JWT', 'username', 'password'];

        Object.keys(params).forEach((key) => {
            const value = params[key as keyof Credentials];
            for (const item of items) {
                if (key.toLowerCase() === item.toLowerCase()) {
                    credentials[item] = value;
                }
            }
        });

        return credentials;
    }

    static weHaveUsableCredentials(credentials: Credentials) {
        let usableCredentials = false;

        if (credentials.authToken) {
            usableCredentials = true;
        } else if (credentials.username && credentials.password) {
            usableCredentials = true;
        }

        if (!usableCredentials) {
            console.error(`EntryApp.configureForDeveloperMachine() - to execute, we need either an authToken or 
                a username/password pair. After combining data from window.pppAppConfig and the query parameters, 
                I still do not have what I need. The app will probably not function correctly if it requires any 
                3P resources.`);
        }

        return usableCredentials;
    }
}
